// The funnel's `sessionStorage` seam, deliberately dependency-free (no React, no
// package internals) so the HOST can import it directly — see the
// `./funnel-storage` subpath export in package.json. The host's sign-out path
// needs `clearPersistedFunnel()`, and importing it from the package root would
// pull the whole lazily-loaded consumer chunk into the main bundle.
//
// PII/PHI: the persisted blob holds the visitor's `/details` (name, email,
// mailing address, dob, sex), `personalization` (reproductive-health intake
// answers) and `intakeDraft` (the same answers, saved one Continue at a time
// before the intake is finished). `sessionStorage` is scoped to origin + TAB,
// not path — so the blob outlives navigation out of the funnel into the rest of
// the app, where any in-origin script can read it. That is why eviction is
// explicit here rather than left to the tab closing (CONAPP-154). See also
// CONAPP-156 for the CSP / XSS hardening that bounds who can read it in the
// first place.

// sessionStorage key, prefixed `consumer.` so it never collides with the host's
// own storage. Holds the full funnel state. The single source of truth for the
// key — the provider and the host both go through this module.
export const FUNNEL_STORAGE_KEY = "consumer.subscriptionFunnel"

// The persisted funnel blob. Every field optional: the shape has changed over
// time and a stale blob from a prior deploy must degrade to "no progress"
// rather than throw. Structurally typed here (not imported from the provider) to
// keep this module free of package internals.
// The BFF's eligibility verdict, duplicated here rather than imported: `packages/consumer`
// (browser) and `packages/bff` (serverless) share no runtime code, only type-only coupling
// through the tRPC client. Keep in step with `EligibilityStatus` in
// `packages/bff/src/eligibility/precheck.ts` and the `status` enum on `eligibilityCheckSchema`
// in `packages/bff/src/trpc/routers/subscription.ts`. A verdict the BFF can emit but this
// union omits is a type error at the `setEligibility` call site, which is the drift alarm.
//
// Declared HERE rather than in `SubscriptionFunnelProvider` (which re-exports it) because
// this module is the leaf: the provider imports it, so the reverse would be a cycle. It is
// also the only description of the persisted blob, which is what carries the verdict across
// a reload.
export type EligibilityStatus = "eligible" | "not-found" | "inactive" | "not-yet-registered" | "already-registered"

export type PersistedFunnelShape = {
  channelPartnerId?: string | null
  // The SERVER-RESOLVED journey for this session (CONAPP-147), not the raw `?journey`
  // param. Persisted so a refresh mid-funnel doesn't re-await resolution, and so a
  // journey CHANGE is detectable: the hydration compares this against the freshly
  // resolved journey and drops the previous journey's intake answers when they differ
  // (they are journey-specific, so carrying them over would route a wrong archetype).
  journey?: string
  // The raw inbound `?journey` param, kept because the server procedures resolve it
  // themselves (they take the raw value plus the partner slug) and the param is gone
  // from the URL after one in-funnel navigation.
  journeyParam?: string
  funnelSessionId?: string
  // The inbound URL query parameters, sanitised and clamped by
  // `readFunnelQueryParams` (see `./funnel-query-params`). Persisted for the same
  // reason `journeyParam` above is: the visitor's original link is gone the moment
  // they refresh on a step, and every funnel event from that point on still has to
  // report the campaign the session came from.
  //
  // Typed loosely (`Record<string, string>`) rather than by the allowlisted keys: a blob
  // persisted by an older bundle can hold others, and every server write re-applies the
  // allowlist anyway.
  queryParams?: Record<string, string>
  planType?: "free" | "paid"
  personalization?: { questionId: string; answer: string }[]
  // The intake's answers as the visitor confirms them, saved on each Continue, including the
  // UI-only choices ("None of these", "I'm not sure") that `personalization` strips. A pick the
  // visitor hasn't continued with is never saved. The intake reopens from this, so leaving it
  // before "Build my plan" (a reload, Back to the details step) keeps what was answered.
  // `personalization` is still only written on "Build my plan", so the plan and everything sent
  // to the server follow that alone. PHI like `personalization`, and evicted with the rest of
  // this blob.
  intakeDraft?: Record<string, string>
  details?: {
    firstName: string
    lastName: string
    email: string
    // Required for the same reason as `sex` below, and absent from a blob persisted before the
    // funnel collected a street address. That degrades the same way: the form prefills
    // `?? profile ?? ""`, and `isSubscriptionDetailsComplete` routes the visitor back to /details.
    address1: string
    address2: string
    city: string
    state: string
    zip: string
    dob: string
    // Typed as required to match `SubscriptionDetails` (the provider hands this
    // straight to `setDetails`), but a blob persisted BEFORE sex shipped has no such
    // key. That degrades to a re-answer rather than a crash: the details form prefills
    // through `?? profile ?? ""`, and because sex IS required,
    // `isSubscriptionDetailsComplete` reports such a blob incomplete and the guard
    // routes the visitor back to /details.
    sex: string
    /**
     * @deprecated CONAPP-205 replaced gender identity with `sex` (assigned sex at
     * birth), which is what the funnel now collects and writes to carrot-app. Nothing
     * reads either key any more.
     *
     * They stay DECLARED, and optional, because they describe data that is already out
     * there: a tab that entered the funnel before this shipped still holds a blob with
     * both keys, and this type is the only description of that blob. Deleting them
     * outright would make the type claim a shape the storage does not have, and would
     * silently drop the two keys from anything that round-trips a legacy blob (the
     * provider re-persists the WHOLE object on every change). Nothing validates or
     * strips unknown keys on read, so leaving them typed costs nothing at runtime.
     *
     * Safe to delete once no live tab can plausibly hold a pre-CONAPP-205 blob —
     * sessionStorage dies with the tab, so a short window after release is enough.
     */
    genderIdentity?: string
    /** @deprecated See `genderIdentity` above — the free-text detail that accompanied it. */
    genderIdentityOther?: string
  } | null
  intakeCompleted?: boolean
  eligibilityCompleted?: boolean
  // Data-processing consent given on /consent (CONAPP-157). Persisted so a refresh
  // on /account doesn't bounce a visitor who already consented back through the step.
  consentCompleted?: boolean
  businessEmail?: string | null
  eligibilityStatus?: EligibilityStatus | null
}

// Reads the persisted blob. Returns `{}` when storage is unavailable (private
// mode / blocked), the value is unparseable, or it parses to anything that isn't a
// plain object — so callers never see a throw.
//
// The non-object check is load-bearing, not defensive noise: `JSON.parse("null")`
// succeeds and yields `null`, which would sail past a bare cast and then throw a
// TypeError in the provider's hydration effect (`persisted.funnelSessionId`) —
// outside this try/catch, taking the whole funnel down. Scalars and arrays are
// harmless by comparison (property reads just yield `undefined`), but they aren't
// this shape either, so they degrade to "no progress" the same way.
export function readPersistedFunnel(): PersistedFunnelShape {
  try {
    const raw = sessionStorage.getItem(FUNNEL_STORAGE_KEY)
    if (raw === null) return {}
    const parsed: unknown = JSON.parse(raw)
    return parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as PersistedFunnelShape)
      : {}
  } catch {
    return {}
  }
}

// Writes the blob. Swallows failures (storage blocked / quota) — funnel state
// simply won't survive a refresh, and no PII/PHI is surfaced on failure.
export function writePersistedFunnel(value: PersistedFunnelShape): void {
  try {
    sessionStorage.setItem(FUNNEL_STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Storage unavailable; in-memory state still drives the current page load.
  }
}

// Evicts the blob. Called on the two events that end the visitor's need for it:
// a completed (paid) funnel, and sign-out — the latter from the HOST, so a
// shared device doesn't leave one member's PII/PHI readable to the next person
// in that tab. Safe to call when nothing is stored, and safe to call repeatedly.
//
// NOTE for the completed-funnel case: removing the key is not sufficient on its
// own, because the provider re-persists on every state change. The provider
// SEALS itself (stops writing) in the same action — see `sealFunnel`.
export function clearPersistedFunnel(): void {
  try {
    sessionStorage.removeItem(FUNNEL_STORAGE_KEY)
  } catch {
    // Storage unavailable ⇒ nothing was persisted to begin with.
  }
}
